import { inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/browser';
import { PayrollCategoryName, YearMonth } from '@wilson/interfaces';
import { DateTimeFormat } from '@wilson/utils';
import { format } from 'date-fns';
import {
  catchError,
  finalize,
  map,
  merge,
  Observable,
  of,
  shareReplay,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { WorkTimeManagementDataService } from '../../../work-time-management-data/work-time-management-data.service';
import { WorkingTimeDashboardContextService } from '../working-time-dashboard/working-time-dashboard-context.service';

@Injectable()
export class WorkingTimeStatsService {
  private readonly workTimeDataService = inject(WorkTimeManagementDataService);
  private readonly dashboardContext = inject(
    WorkingTimeDashboardContextService,
  );
  private readonly toastController = inject(ToastController);
  private readonly translateService = inject(TranslateService);

  readonly currentMonth = format(new Date(), DateTimeFormat.MonthFormat);
  readonly loadingWorktimeStats = signal(true);
  readonly loadingFutureWorkTimeStats = signal(true);

  private readonly fetchTrigger$ = merge(
    this.dashboardContext.monthYear$.pipe(
      map((monthYear) => ({ monthYear, refresh: false as const })),
    ),
    this.dashboardContext.refreshTrigger$.pipe(
      skip(1),
      withLatestFrom(this.dashboardContext.monthYear$),
      map(([, monthYear]) => ({ monthYear, refresh: true as const })),
    ),
  ).pipe(shareReplay(1));

  readonly worktimeStats = toSignal(
    this.fetchTrigger$.pipe(
      tap(() => this.loadingWorktimeStats.set(true)),
      switchMap(({ monthYear, refresh }) =>
        this.getWorktimeStatValues(monthYear, refresh),
      ),
    ),
    { initialValue: [] as number[] },
  );

  readonly futureWorkTimeStats$ = this.fetchTrigger$.pipe(
    tap(() => this.loadingFutureWorkTimeStats.set(true)),
    switchMap(({ monthYear, refresh }) => {
      if (monthYear === this.currentMonth) {
        return this.getFutureWorkTimeStats(monthYear, refresh);
      }
      this.loadingFutureWorkTimeStats.set(false);
      return of(null);
    }),
  );

  private getWorktimeStatValues(
    selectedMonthYear: YearMonth,
    refresh = false,
  ): Observable<number[]> {
    return this.workTimeDataService
      .getWorktimeSummary(
        selectedMonthYear,
        [PayrollCategoryName.IsWorkTime, PayrollCategoryName.PlannedWorktime],
        refresh,
      )
      .pipe(
        map((values) => {
          const minutesPlanned = values['planned-worktime'];
          const minutesWork = values['is-work-time'];
          return [minutesWork, minutesPlanned];
        }),
        catchError((e) => {
          this.handleError(e);
          return of([]);
        }),
        finalize(() => {
          this.loadingWorktimeStats.set(false);
        }),
      );
  }

  private getFutureWorkTimeStats(
    monthYear: YearMonth,
    refresh = false,
  ): Observable<number | null> {
    return this.workTimeDataService
      .getWorktimeUtilizations(monthYear, refresh)
      .pipe(
        map((kpis) => kpis.plannedWorkTimeSumInMinutes),
        catchError((e) => {
          this.handleError(e);
          return of(null);
        }),
        finalize(() => {
          this.loadingFutureWorkTimeStats.set(false);
        }),
      );
  }

  private async handleError(e: Error): Promise<void> {
    captureException(e);
    const toast = await this.toastController.create({
      color: 'danger',
      message: this.translateService.instant('general.errorMessage'),
      duration: 3000,
      position: 'top',
    });
    await toast.present();
  }
}
